import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormLabel,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    TextField,
    Typography
} from '@mui/material';
import { Add, Edit, Delete, Close } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fas } from '@fortawesome/free-solid-svg-icons';
import {
    IconName,
    IconDefinition,
    findIconDefinition,
    library,
} from '@fortawesome/fontawesome-svg-core';
import { useAppDispatch, useAppSelector } from '../../redux-store/hooks';
import { IClientPlaces } from '../../all-types';
import { createPlace, deletePlace, updatePlace } from '../../services';
import { CommonBtn } from '../../components';

library.add(fas);

const PlacesManagement = () => {
    const places = useAppSelector((state) => state.preloadData.places);
    const floors = useAppSelector((state) => state.preloadData.floors);
    const dispatcher = useAppDispatch();

    const [openDialog, setOpenDialog] = useState(false);
    const [currentPlace, setCurrentPlace] = useState<IClientPlaces | null>(null);
    const [iconPickerOpen, setIconPickerOpen] = useState(false);
    const [availableIcons, setAvailableIcons] = useState<IconName[]>([]);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [placeToDelete, setPlaceToDelete] = useState<IClientPlaces | null>(null);
    const [isEditPlaces, setIsEditPlaces] = useState(false);

    useEffect(() => {
        setAvailableIcons([
            'map-marker-alt',
            'building',
            'store',
            'warehouse',
            'chair',
            'users',
            'briefcase',
        ] as IconName[]);
    }, []);

    const getIconDefinition = (iconName: IconName): IconDefinition =>
        findIconDefinition({ prefix: 'fas', iconName });

    const handleOpenCreate = () => {
        setCurrentPlace({
            placeName: '',
            floorId: '',
            placeIcon: 'map-marker-alt',
            placeMap: {
                width: 200,
                height: 200,
                rotation: 0,
                bgImage: '',
                xAxis: 0,
                yAxis: 0,
                bgcolor: '#fff',
                textColor: '#000',
            },
        });
        setOpenDialog(true);
    };

    const handleOpenEdit = (place: IClientPlaces) => {
        setCurrentPlace(place);
        setOpenDialog(true);
        setIsEditPlaces(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setCurrentPlace(null);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name.startsWith('placeMap.')) {
            const field = name.split('.')[1];

            setCurrentPlace((prev) => prev ? ({
                ...prev,
                placeMap: {
                    ...prev.placeMap,
                    [field]: value,
                },
            }) : {
                placeName: '',
                floorId: '',
                placeIcon: 'map-marker-alt',
                placeMap: {
                    width: 200,
                    height: 200,
                    rotation: 0,
                    bgImage: '',
                    xAxis: 0,
                    yAxis: 0,
                    bgcolor: '#fff',
                    textColor: '#000',
                    [field]: value,
                },
            });

        } else {
            setCurrentPlace((prev) => ({
                ...prev!,
                [name]: value,
            }));
        }
    };

    const handleSavePlace = () => {
        try {
            if (!currentPlace) return;
            if (isEditPlaces && currentPlace?._id) {
                dispatcher(updatePlace({ id: currentPlace?._id, place: currentPlace }));
            } else {
                dispatcher(createPlace({ place: currentPlace }));
            }
        } catch (e) {
            console.error(e);
        } finally {
            handleCloseDialog();
            setIsEditPlaces(false);
        }
    };

    const handleDeletePlace = async (place: IClientPlaces) => {
        // Call dispatcher here
        try {
            console.log('Deleting place:', place);
            if (place?._id) {
                await dispatcher(deletePlace(place?._id));
            }
        } catch (e) {
            console.error(e);
        } finally {
            setDeleteConfirmOpen(false);
        }
    };

    const selectIcon = (icon: IconName) => {
        setCurrentPlace((prev) => ({
            ...prev!,
            placeIcon: icon,
        }));
        setIconPickerOpen(false);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                <CommonBtn variant="contained" color="primary" startIcon={<Add />} onClick={handleOpenCreate}>
                    Add New Place
                </CommonBtn>
            </Box>

            {/* Tile Cards */}
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
                {places?.map((place: IClientPlaces) => {
                    const iconDef = getIconDefinition(place.placeIcon as IconName || 'map-marker-alt');
                    return (
                        <Paper
                            key={place.placeName}
                            elevation={3}
                            sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', borderRadius: 5 }}
                        >
                            <FontAwesomeIcon icon={iconDef} size="3x" />
                            <Typography variant="h6" mt={1}>{place.placeName}</Typography>
                            <Typography variant="body2" color="textSecondary">
                                Floor: {floors.find((f) => f?._id === place.floorId)?.floorNo || 'Unknown'}
                            </Typography>
                            <Box mt={2}>
                                <IconButton onClick={() => handleOpenEdit(place)}>
                                    <Edit color="primary" />
                                </IconButton>
                                <IconButton onClick={() => {
                                    setPlaceToDelete(place);
                                    setDeleteConfirmOpen(true);
                                }}>
                                    <Delete color="error" />
                                </IconButton>
                            </Box>
                        </Paper>
                    );
                })}
            </Box>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                <DialogTitle>
                    {currentPlace?._id ? 'Edit Place' : 'Add New Place'}
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseDialog}
                        sx={{ position: 'absolute', right: 8, top: 8, color: 'grey.500' }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <TextField
                        fullWidth
                        label="Place Name"
                        name="placeName"
                        value={currentPlace?.placeName || ''}
                        onChange={handleInputChange}
                        margin="normal"
                    />
                    <TextField
                        select
                        fullWidth
                        label="Select Floor"
                        name="floorId"
                        value={currentPlace?.floorId || ''}
                        onChange={handleInputChange}
                        margin="normal"
                    >
                        {floors.map(floor => (
                            <MenuItem key={floor?.floorNo} value={floor?._id}>
                                Floor {floor.floorNo}
                            </MenuItem>
                        ))}
                    </TextField>

                    {/* <TextField
            fullWidth
            label="Floor ID"
            name="floorId"
            value={currentPlace?.floorId || ''}
            onChange={handleInputChange}
            margin="normal"
          /> */}
                    <Typography variant="subtitle1" sx={{ mt: 2 }}>Place Icon</Typography>
                    <Button variant="outlined" onClick={() => setIconPickerOpen(true)}>
                        {currentPlace?.placeIcon}
                    </Button>
                    <Typography variant="subtitle2" sx={{ mt: 3 }}>Optional Place Map</Typography>
                    <TextField
                        fullWidth
                        label="Width"
                        name="placeMap.width"
                        type="number"
                        value={currentPlace?.placeMap?.width || ''}
                        onChange={handleInputChange}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Height"
                        name="placeMap.height"
                        type="number"
                        value={currentPlace?.placeMap?.height || ''}
                        onChange={handleInputChange}
                        margin="normal"
                    />
                    {/* <TextField
                        fullWidth
                        label="Left"
                        name="placeMap.left"
                        type="number"
                        value={currentPlace?.placeMap?.xAxis || ''}
                        onChange={handleInputChange}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Right"
                        name="placeMap.right"
                        type="number"
                        value={currentPlace?.placeMap?.yAxis || ''}
                        onChange={handleInputChange}
                        margin="normal"
                    /> */}

                    <Grid item xs={12} sm={6}>
                        <FormLabel>Background Color</FormLabel>
                        <TextField
                            fullWidth
                            name="placeMap.bgcolor"
                            type="color"
                            value={currentPlace?.placeMap?.bgcolor || ''}
                            onChange={handleInputChange}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormLabel>Text Color</FormLabel>
                        <TextField
                            fullWidth
                            name="placeMap.textColor"
                            type="color"
                            value={currentPlace?.placeMap?.textColor || ''}
                            onChange={handleInputChange}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <CommonBtn onClick={handleCloseDialog}>Cancel</CommonBtn>
                    <CommonBtn onClick={handleSavePlace} variant="contained" color="primary">Save</CommonBtn>
                </DialogActions>
            </Dialog>

            {/* Icon Picker Dialog */}
            <Dialog open={iconPickerOpen} onClose={() => setIconPickerOpen(false)} maxWidth="md">
                <DialogTitle>Select Icon</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 2 }}>
                        {availableIcons.map((icon) => {
                            const iconDef = getIconDefinition(icon);
                            return (
                                <IconButton
                                    key={icon}
                                    onClick={() => selectIcon(icon)}
                                    sx={{
                                        border: currentPlace?.placeIcon === icon ? '2px solid' : '1px solid',
                                        borderColor: currentPlace?.placeIcon === icon ? 'primary.main' : 'divider',
                                        p: 3,
                                    }}
                                >
                                    <FontAwesomeIcon icon={iconDef} size="2x" />
                                </IconButton>
                            );
                        })}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIconPickerOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete place <strong>{placeToDelete?.placeName}</strong>?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                    <Button
                        onClick={() => placeToDelete && handleDeletePlace(placeToDelete)}
                        color="error"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export { PlacesManagement as PlaceScreen };
